import { useEffect, useMemo, useRef, useState } from "react";

/* ============================================================
   Landing-page Leaderboard — port of the Claude Design
   Leaderboard.html bundle. Replaces the previous static table:
   podium for top 3 (with crown on #1), mini-rows for #4 / #5,
   live payout ticker, count-up PNL, 3D tilt-on-hover with a
   spotlight glow, and "+$N" particles drifting up from each
   card to suggest wins landing in real time.
   ============================================================ */

interface Player {
  rank: 1 | 2 | 3 | 4 | 5;
  handle: string;
  game: "FPS" | "PONG" | "MIXED" | "SNAKE" | "MATH";
  matches: number;
  winrate: number;
  pnl: number;
  streak: number;
  elo: number;
  spark: number[];
  accent: string;
  avatarFrom: string;
  avatarTo: string;
}

const PLAYERS: Player[] = [
  {
    rank: 1, handle: "B4e1…3e2c", game: "FPS", matches: 87,
    winrate: 72.4, pnl: 3420.5, streak: 9, elo: 1842,
    spark: [120, 280, 250, 540, 720, 690, 1080, 1340, 1720, 2080, 2620, 2980, 3120, 3420],
    accent: "#ff5722", avatarFrom: "#ff8a4a", avatarTo: "#a02500",
  },
  {
    rank: 2, handle: "91a4…88f1", game: "MIXED", matches: 152,
    winrate: 64.1, pnl: 2810, streak: 5, elo: 1693,
    spark: [80, 180, 320, 480, 540, 680, 920, 1240, 1480, 1820, 2120, 2380, 2620, 2810],
    accent: "#a4e0ff", avatarFrom: "#cef2ff", avatarTo: "#3a7da3",
  },
  {
    rank: 3, handle: "4fWz…5yqA", game: "PONG", matches: 68,
    winrate: 70.6, pnl: 2105.75, streak: 7, elo: 1721,
    spark: [60, 120, 280, 220, 380, 540, 720, 920, 1180, 1420, 1640, 1820, 1960, 2105],
    accent: "#ffd76b", avatarFrom: "#ffe7a8", avatarTo: "#9a6a00",
  },
  {
    rank: 4, handle: "Ca39…5b22", game: "FPS", matches: 104,
    winrate: 58.7, pnl: 1840, streak: 3, elo: 1604,
    spark: [50, 120, 180, 220, 360, 420, 580, 780, 940, 1180, 1400, 1620, 1740, 1840],
    accent: "#ff8a4a", avatarFrom: "#ffb38a", avatarTo: "#8a3800",
  },
  {
    rank: 5, handle: "d83a…f771", game: "PONG", matches: 93,
    winrate: 61.3, pnl: 1260.4, streak: 4, elo: 1582,
    spark: [40, 80, 160, 200, 280, 380, 480, 620, 780, 920, 1040, 1140, 1220, 1260],
    accent: "#bdc4cf", avatarFrom: "#e7ecf3", avatarTo: "#454a52",
  },
];

const GAME_COLORS: Record<Player["game"], string> = {
  FPS: "#ff5722",
  PONG: "#ffd76b",
  MIXED: "#a4e0ff",
  SNAKE: "#5fcf80",
  MATH: "#e879f9",
};

const RANK_STYLE: Record<
  Player["rank"],
  { color: string; glow: string; label: string }
> = {
  1: { color: "#ffce5b", glow: "rgba(255,206,91,0.5)", label: "01" },
  2: { color: "#d8d3cb", glow: "rgba(216,211,203,0.4)", label: "02" },
  3: { color: "#c98050", glow: "rgba(201,128,80,0.4)", label: "03" },
  4: { color: "#8a7f72", glow: "rgba(138,127,114,0.25)", label: "04" },
  5: { color: "#8a7f72", glow: "rgba(138,127,114,0.25)", label: "05" },
};

// ─────────────────── Hooks ───────────────────

/** Smooth ease-out count-up. Re-animates when target changes. */
function useCountUp(target: number, opts: { duration?: number; delay?: number } = {}): number {
  const { duration = 1800, delay = 0 } = opts;
  const [value, setValue] = useState(0);
  useEffect(() => {
    const from = 0;
    let raf = 0;
    const t0 = performance.now() + delay;
    const tick = (now: number) => {
      const t = Math.min(1, Math.max(0, (now - t0) / duration));
      const ease = t < 0 ? 0 : 1 - Math.pow(1 - t, 3);
      setValue(from + (target - from) * ease);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, delay]);
  return value;
}

/** True at/below the mobile breakpoint. The podium and mini-rows use fixed
 *  pixel layouts that only fit one full-width card per row on phones, so this
 *  drives a vertical stack there. Desktop (>640px) styling is untouched. */
function useIsMobile(query = "(max-width: 640px)"): boolean {
  const [mobile, setMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return mobile;
}

// ─────────────────── Money particles ───────────────────

interface MoneyParticle {
  id: number;
  amount: number;
  tx: number;
  color: string;
}

function FloatingMoney({
  color,
  intensity = 1,
  anchorBottom = 40,
}: {
  color: string;
  intensity?: number;
  anchorBottom?: number;
}) {
  const [particles, setParticles] = useState<MoneyParticle[]>([]);
  const idRef = useRef(0);
  useEffect(() => {
    let mounted = true;
    const timers: number[] = [];
    const spawn = () => {
      if (!mounted) return;
      const amount = [5, 10, 25, 50, 100][Math.floor(Math.random() * 5)];
      const tx = (Math.random() - 0.5) * 60;
      const id = ++idRef.current;
      setParticles((p) => [...p, { id, amount, tx, color }]);
      const drop = window.setTimeout(() => {
        if (mounted) setParticles((p) => p.filter((x) => x.id !== id));
      }, 2200);
      timers.push(drop);
      const next = (1400 + Math.random() * 2400) / intensity;
      const nextTimer = window.setTimeout(spawn, next);
      timers.push(nextTimer);
    };
    const initial = window.setTimeout(spawn, 600 + Math.random() * 1200);
    timers.push(initial);
    return () => {
      mounted = false;
      for (const t of timers) window.clearTimeout(t);
    };
  }, [color, intensity]);
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: anchorBottom,
        pointerEvents: "none",
        display: "flex",
        justifyContent: "center",
        zIndex: 2,
      }}
    >
      {particles.map((p) => (
        <span
          key={p.id}
          className="font-mono"
          style={
            {
              position: "absolute",
              color: p.color,
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: -0.3,
              textShadow: `0 0 12px ${p.color}, 0 1px 0 rgba(0,0,0,0.4)`,
              "--tx": p.tx + "px",
              animation: "lb-floatUp 2.2s cubic-bezier(0.16, 0.84, 0.3, 1) forwards",
            } as React.CSSProperties
          }
        >
          +${p.amount}
        </span>
      ))}
    </div>
  );
}

// ─────────────────── Sparkline ───────────────────

function Sparkline({
  data,
  color,
  width = 240,
  height = 60,
  glow = false,
}: {
  data: number[];
  color: string;
  width?: number;
  height?: number;
  glow?: boolean;
}) {
  const id = useMemo(() => "lb-spark-" + Math.random().toString(36).slice(2, 9), []);
  const max = Math.max(...data);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const points = data.map((v, i): [number, number] => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 6) - 3;
    return [x, y];
  });
  const linePath = "M " + points.map((p) => p.join(" ")).join(" L ");
  const fillPath = linePath + ` L ${width} ${height} L 0 ${height} Z`;
  const head = points[points.length - 1];
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: "block" }}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.4" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={fillPath} fill={`url(#${id})`} />
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth={glow ? 2 : 1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ filter: glow ? `drop-shadow(0 0 6px ${color})` : "none" }}
      />
      <circle cx={head[0]} cy={head[1]} r="3" fill={color} style={{ filter: `drop-shadow(0 0 6px ${color})` }} />
    </svg>
  );
}

// ─────────────────── Avatar orb ───────────────────

function Avatar({ player, size = 56 }: { player: Player; size?: number }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        background: `radial-gradient(circle at 30% 25%, ${player.avatarFrom} 0%, ${player.accent} 45%, ${player.avatarTo} 100%)`,
        border: `1.5px solid ${player.accent}55`,
        boxShadow: `0 0 24px ${player.accent}40, inset 0 -4px 12px rgba(0,0,0,0.3)`,
        position: "relative",
        flexShrink: 0,
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: "12%",
          left: "18%",
          width: "35%",
          height: "25%",
          borderRadius: "50%",
          background: "radial-gradient(ellipse, rgba(255,255,255,0.55), transparent 70%)",
        }}
      />
    </div>
  );
}

// ─────────────────── Stat block ───────────────────

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
      <span
        className="font-mono"
        style={{ fontSize: 9, letterSpacing: 1.3, color: "#5a5247", fontWeight: 500 }}
      >
        {label}
      </span>
      <span
        className="font-mono"
        style={{ fontSize: 13, fontWeight: 600, color: accent ? "#ff5722" : "#f5efe6" }}
      >
        {value}
      </span>
    </div>
  );
}

// ─────────────────── Crown SVG (only on #1) ───────────────────

function CrownSvg() {
  return (
    <svg width="52" height="40" viewBox="0 0 72 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="lb-crown-gold" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fff3b8" />
          <stop offset="40%" stopColor="#ffd76b" />
          <stop offset="100%" stopColor="#c0890a" />
        </linearGradient>
        <linearGradient id="lb-crown-gold-dark" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#d99b1c" />
          <stop offset="100%" stopColor="#7a5200" />
        </linearGradient>
        <radialGradient id="lb-crown-ruby" cx="50%" cy="35%" r="60%">
          <stop offset="0%" stopColor="#ffb6c0" />
          <stop offset="60%" stopColor="#ff3a5e" />
          <stop offset="100%" stopColor="#7a0020" />
        </radialGradient>
        <radialGradient id="lb-crown-emerald" cx="50%" cy="35%" r="60%">
          <stop offset="0%" stopColor="#b6ffd5" />
          <stop offset="60%" stopColor="#22c97a" />
          <stop offset="100%" stopColor="#0a4d2c" />
        </radialGradient>
      </defs>
      <path
        d="M 6 38 L 10 18 L 20 30 L 26 8 L 36 26 L 46 8 L 52 30 L 62 18 L 66 38 Z"
        fill="url(#lb-crown-gold)"
        stroke="url(#lb-crown-gold-dark)"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <rect
        x="6"
        y="38"
        width="60"
        height="10"
        rx="2"
        fill="url(#lb-crown-gold)"
        stroke="url(#lb-crown-gold-dark)"
        strokeWidth="1.2"
      />
      <line x1="9" y1="43" x2="63" y2="43" stroke="#c0890a" strokeWidth="0.8" opacity="0.7" />
      <circle cx="10" cy="18" r="2.2" fill="url(#lb-crown-emerald)" stroke="#0a4d2c" strokeWidth="0.4" />
      <circle cx="36" cy="8" r="2.8" fill="url(#lb-crown-ruby)" stroke="#7a0020" strokeWidth="0.4" />
      <circle cx="62" cy="18" r="2.2" fill="url(#lb-crown-emerald)" stroke="#0a4d2c" strokeWidth="0.4" />
      <circle cx="20" cy="43" r="1.6" fill="url(#lb-crown-ruby)" />
      <circle cx="36" cy="43" r="1.8" fill="url(#lb-crown-emerald)" />
      <circle cx="52" cy="43" r="1.6" fill="url(#lb-crown-ruby)" />
      <path
        d="M 9 22 L 11 19 L 18 26 L 23 14 L 25 10"
        stroke="#fff8d0"
        strokeWidth="1.2"
        fill="none"
        strokeLinecap="round"
        opacity="0.7"
      />
      <path
        d="M 47 11 L 49 13 L 53 26 L 59 19 L 62 22"
        stroke="#fff8d0"
        strokeWidth="1.2"
        fill="none"
        strokeLinecap="round"
        opacity="0.5"
      />
      <line x1="9" y1="40" x2="63" y2="40" stroke="#fff8d0" strokeWidth="0.8" opacity="0.5" />
    </svg>
  );
}

// ─────────────────── Podium card (top 3) ───────────────────

function PodiumCard({
  player,
  raised = false,
  isMobile = false,
}: {
  player: Player;
  raised?: boolean;
  isMobile?: boolean;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [hover, setHover] = useState(false);
  const [m, setM] = useState({ x: 0.5, y: 0.5 });
  const pnl = useCountUp(player.pnl, { duration: 1800, delay: player.rank * 120 });
  const r = RANK_STYLE[player.rank];

  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setM({ x: (e.clientX - rect.left) / rect.width, y: (e.clientY - rect.top) / rect.height });
  };
  const rotX = (0.5 - m.y) * (hover ? 10 : 0);
  const rotY = (m.x - 0.5) * (hover ? 14 : 0);
  const lift = hover ? 14 : 0;

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => {
        setHover(false);
        setM({ x: 0.5, y: 0.5 });
      }}
      style={{
        position: "relative",
        flex: isMobile ? "0 1 auto" : "1 1 0",
        width: isMobile ? "100%" : undefined,
        maxWidth: 320,
        // Stagger only matters in the side-by-side podium; stacked cards sit flush.
        marginTop: isMobile ? 0 : raised ? 0 : 40,
        // Order #1 → #2 → #3 top-to-bottom when stacked (DOM order is #2,#1,#3).
        order: isMobile ? player.rank : undefined,
        perspective: 1200,
        cursor: "pointer",
        zIndex: hover ? 5 : 1,
        transformStyle: "preserve-3d",
      }}
    >
      <div
        style={{
          position: "relative",
          height: raised ? 460 : 410,
          borderRadius: 24,
          background:
            `radial-gradient(ellipse 80% 50% at 50% 0%, ${player.accent}20, transparent 60%), ` +
            "linear-gradient(180deg, rgba(28,24,20,0.95), rgba(14,11,9,0.95))",
          border: `1px solid ${hover ? player.accent : "#322a22"}`,
          transform: `rotateX(${rotX}deg) rotateY(${rotY}deg) translateZ(${lift}px) scale(${
            hover ? 1.03 : 1
          })`,
          transformStyle: "preserve-3d",
          transition:
            "transform 220ms cubic-bezier(0.16, 0.84, 0.3, 1), border-color 200ms ease, box-shadow 240ms ease",
          boxShadow: hover
            ? `0 40px 100px -30px ${player.accent}60, 0 0 0 1px ${player.accent}40 inset, inset 0 1px 0 ${player.accent}50`
            : "0 20px 60px -30px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.04)",
        }}
      >
        {/* Mouse-following spotlight */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(circle 240px at ${m.x * 100}% ${m.y * 100}%, ${player.accent}25, transparent 70%)`,
            opacity: hover ? 1 : 0,
            transition: "opacity 240ms ease",
            pointerEvents: "none",
          }}
        />

        {/* Top accent line */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg, transparent, ${r.color}, transparent)`,
            opacity: 0.7,
          }}
        />

        {/* Crown on #1 */}
        {raised && (
          <div
            style={{
              position: "absolute",
              top: 18,
              left: "50%",
              transform: "translateX(-50%) translateZ(30px)",
              width: 52,
              height: 40,
              filter:
                "drop-shadow(0 6px 16px rgba(255,206,91,0.5)) drop-shadow(0 0 24px rgba(255,206,91,0.3))",
              pointerEvents: "none",
              zIndex: 10,
            }}
          >
            <CrownSvg />
          </div>
        )}

        {/* Rank badge */}
        <div
          style={{
            position: "absolute",
            top: 20,
            left: 20,
            display: "flex",
            alignItems: "center",
            gap: 8,
            transform: "translateZ(10px)",
          }}
        >
          <span
            className="font-mono"
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: r.color,
              letterSpacing: 1,
              textShadow: `0 0 12px ${r.glow}`,
            }}
          >
            #{r.label}
          </span>
        </div>

        {/* ELO badge */}
        <div
          style={{
            position: "absolute",
            top: 20,
            right: 20,
            display: "flex",
            alignItems: "center",
            gap: 6,
            transform: "translateZ(10px)",
          }}
        >
          <span
            className="font-mono"
            style={{ fontSize: 10, color: "#5a5247", letterSpacing: 1.2 }}
          >
            ELO
          </span>
          <span
            className="font-mono"
            style={{ fontSize: 11, color: "#f5efe6", fontWeight: 600 }}
          >
            {player.elo}
          </span>
        </div>

        {/* Avatar + handle stack */}
        <div
          style={{
            position: "absolute",
            top: raised ? 68 : 60,
            left: 0,
            right: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 14,
            transform: "translateZ(20px)",
          }}
        >
          <Avatar player={player} size={raised ? 84 : 72} />
          <div
            className="font-mono"
            style={{
              fontSize: raised ? 16 : 15,
              fontWeight: 600,
              color: "#f5efe6",
              letterSpacing: -0.2,
            }}
          >
            {player.handle}
          </div>
          <span
            className="font-mono"
            style={{
              padding: "4px 10px",
              borderRadius: 999,
              background: `${GAME_COLORS[player.game]}15`,
              border: `1px solid ${GAME_COLORS[player.game]}40`,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: 1.3,
              color: GAME_COLORS[player.game],
            }}
          >
            {player.game}
          </span>
        </div>

        {/* PNL */}
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: raised ? 250 : 230,
            textAlign: "center",
            transform: "translateZ(30px)",
          }}
        >
          <div
            className="font-mono"
            style={{
              fontSize: 10,
              letterSpacing: 1.6,
              color: "#5a5247",
              marginBottom: 4,
            }}
          >
            7-DAY NET
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "center",
              gap: 4,
            }}
          >
            <span
              className="font-mono"
              style={{
                fontSize: raised ? 16 : 14,
                color: "#5fcf80",
                fontWeight: 600,
                transform: "translateY(-12px)",
              }}
            >
              +$
            </span>
            <span
              className="font-mono"
              style={{
                fontSize: raised ? 52 : 44,
                fontWeight: 800,
                background: "linear-gradient(180deg, #d2ffce 0%, #5fcf80 60%, #2a9952 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                letterSpacing: -2,
                lineHeight: 0.95,
                textShadow: "0 0 40px rgba(95,207,128,0.15)",
              }}
            >
              {formatPnl(pnl)}
            </span>
          </div>
        </div>

        {/* Sparkline */}
        <div
          style={{
            position: "absolute",
            left: 16,
            right: 16,
            top: raised ? 340 : 310,
            transform: "translateZ(15px)",
          }}
        >
          <Sparkline
            data={player.spark}
            color={player.accent}
            width={raised ? 288 : 248}
            height={48}
            glow={hover}
          />
        </div>

        {/* Stats footer */}
        <div
          style={{
            position: "absolute",
            left: 20,
            right: 20,
            bottom: 20,
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 8,
            paddingTop: 14,
            borderTop: "1px solid #25201a",
            transform: "translateZ(15px)",
          }}
        >
          <Stat label="MATCHES" value={String(player.matches)} />
          <Stat label="WIN" value={`${player.winrate}%`} />
          <Stat label="STREAK" value={`${player.streak}W`} accent />
        </div>

        <FloatingMoney
          color={player.accent}
          intensity={1.0}
          anchorBottom={raised ? 110 : 100}
        />
      </div>
    </div>
  );
}

// ─────────────────── Mini row (#4, #5) ───────────────────

function MiniRow({ player, isMobile = false }: { player: Player; isMobile?: boolean }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [hover, setHover] = useState(false);
  const [m, setM] = useState({ x: 0.5, y: 0.5 });
  const pnl = useCountUp(player.pnl, { duration: 1800, delay: 400 + player.rank * 80 });
  const r = RANK_STYLE[player.rank];

  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setM({ x: (e.clientX - rect.left) / rect.width, y: (e.clientY - rect.top) / rect.height });
  };
  const rotX = (0.5 - m.y) * (hover ? 5 : 0);
  const rotY = (m.x - 0.5) * (hover ? 7 : 0);

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => {
        setHover(false);
        setM({ x: 0.5, y: 0.5 });
      }}
      style={{
        position: "relative",
        flex: "1 1 0",
        perspective: 900,
        cursor: "pointer",
        zIndex: hover ? 5 : 1,
      }}
    >
      <div
        style={{
          position: "relative",
          padding: "18px 22px",
          borderRadius: 16,
          background:
            `radial-gradient(ellipse 80% 100% at 0% 50%, ${player.accent}10, transparent 60%), ` +
            "linear-gradient(180deg, rgba(28,24,20,0.85), rgba(14,11,9,0.95))",
          border: `1px solid ${hover ? player.accent : "#322a22"}`,
          transform: `rotateX(${rotX}deg) rotateY(${rotY}deg) translateZ(${
            hover ? 8 : 0
          }px) scale(${hover ? 1.015 : 1})`,
          transition:
            "transform 220ms cubic-bezier(0.16, 0.84, 0.3, 1), border-color 200ms ease, box-shadow 240ms ease",
          boxShadow: hover
            ? `0 24px 60px -20px ${player.accent}40, inset 0 1px 0 ${player.accent}30`
            : "0 8px 28px -16px rgba(0,0,0,0.6)",
          display: "grid",
          // Mobile: a phone-width row can't hold rank+avatar+handle+stats+PNL
          // side by side, so drop the sparkline and fold PNL onto a second row
          // (rank/avatar span both rows on the left). Desktop keeps one row.
          gridTemplateColumns: isMobile ? "auto auto 1fr" : "auto auto 1fr auto auto",
          gap: isMobile ? "10px 12px" : 18,
          alignItems: "center",
          overflow: "hidden",
        }}
      >
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(circle 180px at ${m.x * 100}% ${m.y * 100}%, ${player.accent}18, transparent 70%)`,
            opacity: hover ? 1 : 0,
            transition: "opacity 240ms ease",
            pointerEvents: "none",
          }}
        />

        <span
          className="font-mono"
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: r.color,
            letterSpacing: 1,
            minWidth: 22,
            gridRow: isMobile ? "1 / 3" : undefined,
            alignSelf: "center",
          }}
        >
          #{r.label}
        </span>

        <div style={isMobile ? { gridRow: "1 / 3", display: "flex", alignItems: "center" } : undefined}>
          <Avatar player={player} size={44} />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
          <span
            className="font-mono"
            style={{ fontSize: 14, fontWeight: 600, color: "#f5efe6", whiteSpace: "nowrap" }}
          >
            {player.handle}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              className="font-mono"
              style={{
                padding: "2px 8px",
                borderRadius: 999,
                fontSize: 9,
                fontWeight: 600,
                letterSpacing: 1.2,
                background: `${GAME_COLORS[player.game]}15`,
                border: `1px solid ${GAME_COLORS[player.game]}40`,
                color: GAME_COLORS[player.game],
              }}
            >
              {player.game}
            </span>
            <span className="font-mono" style={{ fontSize: 10, color: "#5a5247" }}>
              {player.matches} M · {player.winrate}% W · {player.streak}W STK
            </span>
          </div>
        </div>

        {!isMobile && (
          <Sparkline data={player.spark} color={player.accent} width={120} height={36} glow={hover} />
        )}

        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 3,
            minWidth: isMobile ? 0 : 130,
            justifyContent: "flex-end",
            // Mobile: PNL drops to row 2 under the handle, left-aligned.
            gridColumn: isMobile ? 3 : undefined,
            justifySelf: isMobile ? "start" : undefined,
            marginTop: isMobile ? 2 : undefined,
          }}
        >
          <span className="font-mono" style={{ fontSize: 13, color: "#5fcf80", fontWeight: 600 }}>
            +$
          </span>
          <span
            className="font-mono"
            style={{ fontSize: 24, fontWeight: 700, color: "#5fcf80", letterSpacing: -0.5 }}
          >
            {formatPnl(pnl)}
          </span>
        </div>

        <FloatingMoney color={player.accent} intensity={0.5} anchorBottom={50} />
      </div>
    </div>
  );
}

// ─────────────────── Live payout ticker ───────────────────

function LivePayoutCounter({ initial = 74_830 }: { initial?: number }) {
  const [target, setTarget] = useState(initial);
  const value = useCountUp(target, { duration: 1200 });

  useEffect(() => {
    let mounted = true;
    let nextTimer = 0;
    const tick = () => {
      if (!mounted) return;
      const amount = [5, 10, 25, 50, 75, 100][Math.floor(Math.random() * 6)];
      setTarget((t) => t + amount);
      nextTimer = window.setTimeout(tick, 2400 + Math.random() * 3000);
    };
    const initialTimer = window.setTimeout(tick, 2200);
    return () => {
      mounted = false;
      window.clearTimeout(initialTimer);
      window.clearTimeout(nextTimer);
    };
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
      <span
        className="font-mono"
        style={{
          fontSize: 11,
          letterSpacing: 1.6,
          color: "#5a5247",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: 999,
            background: "#5fcf80",
            boxShadow: "0 0 0 3px rgba(95,207,128,0.18)",
            animation: "lb-pulse 1.4s ease-in-out infinite",
          }}
        />
        PAID OUT THIS WEEK
      </span>
      <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
        <span
          className="font-mono"
          style={{
            fontSize: 22,
            color: "#8a7f72",
            fontWeight: 500,
            transform: "translateY(-6px)",
          }}
        >
          $
        </span>
        <span
          className="font-mono"
          style={{
            fontSize: 40,
            fontWeight: 800,
            background: "linear-gradient(180deg, #fff8ef 0%, #ffd1ad 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            letterSpacing: -1.5,
            lineHeight: 1,
            textShadow: "0 0 30px rgba(255,140,80,0.25)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {Math.round(value).toLocaleString()}
        </span>
      </div>
    </div>
  );
}

// ─────────────────── CTA button ───────────────────

function CtaButton() {
  const [hover, setHover] = useState(false);
  const onClick = () => {
    const games = document.getElementById("games-grid");
    if (games) games.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        appearance: "none",
        border: "none",
        borderRadius: 12,
        padding: "14px 22px",
        cursor: "pointer",
        background: "linear-gradient(180deg, #ff7b3d 0%, #ff5722 55%, #e94a18 100%)",
        color: "#fff",
        fontFamily: "inherit",
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: 0.2,
        boxShadow: hover
          ? "0 18px 50px -18px rgba(255,87,34,0.7), 0 0 0 1px rgba(255,140,80,0.4) inset, 0 1px 0 rgba(255,255,255,0.15) inset"
          : "0 10px 30px -14px rgba(255,87,34,0.6), 0 0 0 1px rgba(255,140,80,0.25) inset, 0 1px 0 rgba(255,255,255,0.12) inset",
        transform: hover ? "translateY(-1px)" : "translateY(0)",
        transition: "all 180ms ease",
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <span>Play now</span>
      <span
        style={{
          width: 24,
          height: 24,
          borderRadius: 999,
          background: "rgba(0,0,0,0.28)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid rgba(255,255,255,0.16)",
          transition: "transform 200ms ease",
          transform: hover ? "translateX(3px)" : "translateX(0)",
        }}
      >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
          <path d="M5 12h14M13 6l6 6-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    </button>
  );
}

// ─────────────────── Helpers ───────────────────

function formatPnl(n: number): string {
  return n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

// ─────────────────── Main export ───────────────────

export default function Leaderboard() {
  const isMobile = useIsMobile();
  return (
    <section
      id="leaderboard"
      style={{
        position: "relative",
        width: "100%",
        maxWidth: 1400,
        margin: "0 auto",
        padding: "48px clamp(20px, 4vw, 48px) 64px",
        color: "#f5efe6",
        fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes lb-floatUp {
              0%   { transform: translate(var(--tx, 0px), 0) scale(0.85); opacity: 0; }
              14%  { opacity: 1; transform: translate(var(--tx, 0px), -8px) scale(1); }
              100% { transform: translate(var(--tx, 0px), -130px) scale(0.95); opacity: 0; }
            }
            @keyframes lb-pulse {
              0%, 100% { transform: scale(1); opacity: 1; }
              50%      { transform: scale(0.7); opacity: 0.5; }
            }
          `,
        }}
      />

      {/* Header — eyebrow + headline + live payout */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 24,
          marginBottom: 60,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "6px 12px",
              borderRadius: 999,
              border: "1px solid #322a22",
              background: "rgba(22,19,17,0.6)",
              marginBottom: 16,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: 999,
                background: "#ff5722",
                boxShadow: "0 0 0 3px rgba(255,87,34,0.2)",
                animation: "lb-pulse 1.4s ease-in-out infinite",
              }}
            />
            <span
              className="font-mono"
              style={{
                fontSize: 11,
                letterSpacing: 1.6,
                color: "#ff5722",
                fontWeight: 600,
              }}
            >
              LEADERBOARD · 7-DAY
            </span>
          </div>
          <h2
            style={{
              fontSize: "clamp(40px, 5.5vw, 72px)",
              fontWeight: 700,
              margin: 0,
              lineHeight: 1,
              letterSpacing: -2.5,
              textWrap: "balance" as React.CSSProperties["textWrap"],
            }}
          >
            Top players <br />
            <span
              style={{
                fontFamily: "'Instrument Serif', 'Fraunces', Georgia, serif",
                fontStyle: "italic",
                fontWeight: 400,
                background: "linear-gradient(180deg, #ff8a4a 0%, #ff5722 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              cashing in
            </span>{" "}
            this week.
          </h2>
        </div>
        <LivePayoutCounter />
      </div>

      {/* Podium — top 3 with #1 raised in the middle */}
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "center" : "flex-start",
          justifyContent: "center",
          gap: 24,
          marginBottom: 24,
          flexWrap: "wrap",
        }}
      >
        <PodiumCard player={PLAYERS[1]} isMobile={isMobile} />
        <PodiumCard player={PLAYERS[0]} raised isMobile={isMobile} />
        <PodiumCard player={PLAYERS[2]} isMobile={isMobile} />
      </div>

      {/* #4 and #5 */}
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 14,
          marginBottom: 32,
          flexWrap: "wrap",
        }}
      >
        <MiniRow player={PLAYERS[3]} isMobile={isMobile} />
        <MiniRow player={PLAYERS[4]} isMobile={isMobile} />
      </div>

      {/* Bottom CTA strip */}
      <div
        style={{
          position: "relative",
          marginTop: 28,
          borderRadius: 18,
          border: "1px solid rgba(255, 87, 34, 0.25)",
          background:
            "radial-gradient(ellipse 60% 100% at 0% 50%, rgba(255,87,34,0.10), transparent 65%), " +
            "radial-gradient(ellipse 50% 100% at 100% 50%, rgba(255,87,34,0.08), transparent 60%), " +
            "linear-gradient(180deg, rgba(22,19,17,0.85), rgba(14,11,9,0.95))",
          padding: "24px 28px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 24,
          overflow: "hidden",
          flexWrap: "wrap",
        }}
      >
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 1,
            background: "linear-gradient(90deg, transparent, #ff5722, transparent)",
            opacity: 0.5,
          }}
        />

        <div>
          <div
            style={{
              fontFamily: "'Instrument Serif', 'Fraunces', Georgia, serif",
              fontSize: 32,
              fontStyle: "italic",
              fontWeight: 400,
              lineHeight: 1.05,
              letterSpacing: -0.5,
              color: "#f5efe6",
            }}
          >
            Be on this list next week.
          </div>
          <div
            className="font-mono"
            style={{
              fontSize: 11.5,
              color: "#8a7f72",
              letterSpacing: 0.4,
              marginTop: 6,
            }}
          >
            Pick a game, set your stake, win the pot. 10% house fee, on-chain settlement.
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ textAlign: "right" }}>
            <div
              className="font-mono"
              style={{ fontSize: 10, letterSpacing: 1.3, color: "#5a5247" }}
            >
              YOUR RANK
            </div>
            <div
              className="font-mono"
              style={{ fontSize: 22, fontWeight: 700, color: "#f5efe6" }}
            >
              #248
            </div>
          </div>
          <CtaButton />
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          marginTop: 22,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <span
          className="font-mono"
          style={{ fontSize: 11, color: "#5a5247", letterSpacing: 0.5 }}
        >
          Showing top 5 of 8,420 active players
        </span>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontFamily: "'Geist Mono', ui-monospace, monospace",
            fontSize: 11,
            color: "#8a7f72",
          }}
        >
          View full leaderboard
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
            <path
              d="M5 12h14M13 6l6 6-6 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </div>
    </section>
  );
}
